<script setup lang="ts">
import { computed, toValue } from "vue";
import { useI18n } from "vue-i18n";
import { useSortable } from "../../composables/Query/sortable";
import VDropdown from "../Dropdown/VDropdown.vue";
import VDropdownItem from "../DropdownItem/VDropdownItem.vue";
import VIcon from "../Icon/VIcon.vue";
import VToolbarTrigger from "./VToolbarTrigger.vue";

const { t } = useI18n();

const {
  sort,
  sortableFields,
  sortDirection,
  isAtDefaultSort,
  resetToDefaultSort,
} = useSortable();

const sortFieldItems = computed(() => {
  return Object.entries(toValue(sortableFields)).map(([value, label]) => ({
    id: value,
    name: label ?? value,
  }));
});

const isSortActive = computed(() => !!sort.value);

const hasSortField = computed(() => !!sort.value);

const sortTooltip = computed(() => t("base.sort.tooltip"));

const chooseFieldFirstTooltip = computed(() => t("base.sort.choose_field_first"));

const alreadyDefaultTooltip = computed(() => t("base.sort.already_default"));

const isAscending = computed(() => sortDirection.value === "ASC");

const sortDirectionLabel = computed(() =>
  isAscending.value
    ? t("base.sort.ascending")
    : t("base.sort.descending"),
);

const sortDirectionIcon = computed(() =>
  isAscending.value
    ? "arrow-up-wide-narrow"
    : "arrow-down-wide-narrow",
);

const sortButtonIcon = computed(() => {
  if (!sort.value) {
    return "arrow-down-up";
  }
  return sortDirectionIcon.value;
});

function toggleSortDirection() {
  if (!hasSortField.value) {
    return;
  }
  sortDirection.value = isAscending.value ? "DESC" : "ASC";
}

function selectSortField(field: string) {
  sort.value = field;
}

function onReset() {
  if (isAtDefaultSort.value) {
    return;
  }
  resetToDefaultSort();
}
</script>

<template>
  <VDropdown
    theme="verbleif-popper"
    placement="bottom-start"
    class="shrink-0"
  >
    <template #popper="{ hide }">
      <div class="options min-w-[180px]">
        <VDropdownItem
          :close-on-click="!isAtDefaultSort"
          :disabled="isAtDefaultSort"
          :disabled-notice="alreadyDefaultTooltip"
          :text="t('base.sort.reset')"
          icon="undo"
          :hide="hide"
          @click="onReset"
        />
        <VTooltip
          :content="chooseFieldFirstTooltip"
          placement="right"
          theme="verbleif-tooltip-solid"
          :disabled="hasSortField"
        >
          <template #reference>
            <span class="block w-full">
              <button
                type="button"
                class="flex w-full items-center gap-2.5 rounded-[5px] px-2 py-2 text-left text-sm transition-colors focus-visible:outline-hidden focus-visible:bg-gray-100 dark:focus-visible:bg-dark-800/70"
                :class="hasSortField
                  ? 'text-(--primary) hover:bg-gray-100 dark:hover:bg-dark-800/70'
                  : 'pointer-events-none cursor-not-allowed text-gray-400 opacity-60 dark:text-gray-500'"
                role="menuitem"
                tabindex="-1"
                data-menu-item
                :aria-label="hasSortField ? sortDirectionLabel : chooseFieldFirstTooltip"
                :aria-disabled="!hasSortField"
                @click="toggleSortDirection"
              >
                <VIcon
                  class="w-4 shrink-0 text-base"
                  :name="sortDirectionIcon"
                />
                <span class="min-w-0 flex-1 font-normal">
                  {{ sortDirectionLabel }}
                </span>
                <VIcon
                  class="shrink-0 text-xs text-gray-400 dark:text-gray-500"
                  name="arrow-right-left"
                  aria-hidden="true"
                />
              </button>
            </span>
          </template>
        </VTooltip>
        <div class="mx-2 my-1.5 border-t border-gray-200 dark:border-dark-600" role="separator" />
        <VDropdownItem
          v-for="field in sortFieldItems"
          :key="field.id"
          close-on-click
          :text="field.name"
          :selected="sort === field.id"
          selection="single"
          :hide="hide"
          @click="selectSortField(field.id)"
        />
      </div>
    </template>
    <VToolbarTrigger
      :icon="sortButtonIcon"
      :label="sortTooltip"
      :active="isSortActive"
      has-popup
    />
  </VDropdown>
</template>
